import { ChangeDetectorRef, Component, Input, OnInit } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { fuseAnimations } from "@fuse/animations";
import { FuseTranslationLoaderService } from "@fuse/services/translation-loader.service";
import { AlarmModel } from "app/models/alarm.model";
import { ProjectDeviceModel } from "app/models/projectDevice.model";
import { AlarmService } from "app/services/api/alarm.service";
import { ToastrService } from "ngx-toastr";
import { AlarmAddEditComponent } from "../../../../components/alarm-add-edit/alarm-add-edit.component";
import { ProjectModel } from "../../../../models/project.model";
import { ProjectAlarmModel } from "../../../../models/projectAlarm.model";
import { ProjectAlarmService } from "../../../../services/api/projectAlarm.service";
import { ContextService } from "../../../../services/context.service";
import { ProjectService } from "../../../../services/api/project.service";
import { FuseConfirmDialogComponent } from "../../../../../@fuse/components/confirm-dialog/confirm-dialog.component";
import { locale as catalan } from "../../i18n/ca";
import { locale as english } from "../../i18n/en";
import { locale as spanish } from "../../i18n/es";
import { cloneAlarmForDuplicate } from "app/shared/alarm-duplication";
import { AlarmAction } from "app/components/alarm-add-edit/alarm-add-edit.component";

@Component({
    selector: "app-device-alarms",
    templateUrl: "./device-alarms.component.html",
    styleUrls: ["./device-alarms.component.scss"],
    animations: fuseAnimations,
    standalone: false,
})
export class DeviceAlarmsComponent implements OnInit {
    @Input() device: ProjectDeviceModel;
    private dialogRef: MatDialogRef<AlarmAddEditComponent>;
    alarms: AlarmModel[] = [];
    projectAlarms: AlarmModel[] = [];
    project: ProjectModel | null = null;
    alarmsLoaded = false;
    projectAlarmsLoaded = false;

    constructor(
        private alarmService: AlarmService,
        private projectAlarmService: ProjectAlarmService,
        private projectService: ProjectService,
        private contextService: ContextService,
        private _fuseTranslationLoaderService: FuseTranslationLoaderService,
        private _matDialog: MatDialog,
        private toastr: ToastrService,
        private router: Router,
        private cdr: ChangeDetectorRef,
    ) {
        this._fuseTranslationLoaderService.loadTranslations(english, spanish, catalan);
    }

    ngOnInit() {
        if (!this.device?.id) {
            return;
        }
        this.projectService.get(this.device.project).subscribe((project) => {
            this.project = project;
            this.cdr.detectChanges();
        });
        this.loadData();
    }

    private loadData() {
        this.alarmsLoaded = false;
        this.projectAlarmsLoaded = false;
        this.alarmService.getAllDevice(this.device.id).subscribe(
            (alarms) => {
                this.alarms = alarms;
                this.alarmsLoaded = true;
                this.cdr.detectChanges();
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error: " + error.message);
                this.cdr.detectChanges();
            },
        );

        const currentProject = this.contextService.currentProject;
        this.projectAlarmService.getAllProject(currentProject, this.device.id).subscribe(
            (alarms) => {
                this.projectAlarms = alarms;
                this.projectAlarmsLoaded = true;
                this.cdr.detectChanges();
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error: " + error.message);
                this.cdr.detectChanges();
            },
        );
    }

    public addAlarm() {
        const data = { device: this.device };
        this.dialogRef = this.openAlarmPanel(data);
        this.dialogRef.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }
            const alarm: AlarmModel = result.alarm;
            this.alarmService.create(this.device.id, alarm).subscribe(
                () => {
                    this.loadData();
                },
                (error) => {
                    console.log(error);
                    this.toastr.error("Error: " + error.message);
                },
            );
        });
    }

    public onAlarmClick(alarm: AlarmModel) {
        const data = { alarm, device: this.device };
        this.dialogRef = this.openAlarmPanel(data);
        this.dialogRef.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }
            const editedAlarm = result.alarm;
            this.alarmService.edit(this.device.id, editedAlarm).subscribe(
                () => {
                    this.loadData();
                },
                (error) => {
                    console.log(error);
                    this.toastr.error("Error: " + error.message);
                },
            );
        });
    }

    public onAlarmDuplicate(alarm: AlarmModel) {
        const data = {
            action: AlarmAction.NEW,
            alarm: cloneAlarmForDuplicate(alarm),
            device: this.device,
        };
        this.dialogRef = this.openAlarmPanel(data);
        this.dialogRef.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }
            const duplicatedAlarm = result.alarm;
            this.alarmService.create(this.device.id, duplicatedAlarm).subscribe(
                () => {
                    this.loadData();
                },
                (error) => {
                    console.log(error);
                    this.toastr.error("Error: " + error.message);
                },
            );
        });
    }

    public onProjectAlarmClick(alarm: ProjectAlarmModel): void {
        const project =
            this.project ??
            Object.assign(new ProjectModel(), {
                id: this.contextService.currentProject,
            });
        const data = { alarm, project };
        this.dialogRef = this.openAlarmPanel(data);
        this.dialogRef.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }
            const editedAlarm = result.alarm;
            this.projectAlarmService.edit(this.contextService.currentProject, editedAlarm).subscribe(
                () => {
                    this.loadData();
                },
                (error) => {
                    console.log(error);
                    this.toastr.error("Error: " + error.message);
                },
            );
        });
    }

    public onProjectAlarmDuplicate(alarm: ProjectAlarmModel): void {
        const project =
            this.project ??
            Object.assign(new ProjectModel(), {
                id: this.contextService.currentProject,
            });
        const data = {
            action: AlarmAction.NEW,
            alarm: cloneAlarmForDuplicate(alarm),
            project,
        };
        this.dialogRef = this.openAlarmPanel(data);
        this.dialogRef.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }
            const duplicatedAlarm = result.alarm;
            this.projectAlarmService.create(project.id, duplicatedAlarm).subscribe(
                () => {
                    this.loadData();
                },
                (error) => {
                    console.log(error);
                    this.toastr.error("Error: " + error.message);
                },
            );
        });
    }

    public onAlarmDelete(alarm: AlarmModel) {
        this.alarmService.getActiveEvents(this.device.id, alarm.id).subscribe(
            (result) => {
                if (result.activeEvents > 0) {
                    const message = this._fuseTranslationLoaderService.translateService.instant("ALARM.AlarmDeleteMessage", {
                        activeEvents: result.activeEvents,
                    });
                    let confirmDialogRef = this._matDialog.open(FuseConfirmDialogComponent, {
                        disableClose: false,
                    });
                    confirmDialogRef.componentInstance.title = "ALARM.AlarmDeleteTitle";
                    confirmDialogRef.componentInstance.confirmMessage = message;
                    confirmDialogRef.afterClosed().subscribe((confirm) => {
                        if (confirm) {
                            this.alarmDeleteAction(alarm);
                        }
                        confirmDialogRef = null;
                    });
                } else {
                    this.alarmDeleteAction(alarm);
                }
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error: " + error.message);
            },
        );
    }

    public alarmDeleteAction(alarm) {
        this.alarmService.delete(this.device.id, alarm.id).subscribe(
            () => {
                this.loadData();
            },
            (error) => {
                console.log(error);
                this.toastr.error("Error: " + error.message);
            },
        );
    }

    public onProjectAlarmDelete(alarm: ProjectAlarmModel): void {
        this.projectAlarmService.delete(this.contextService.currentProject, alarm.id).subscribe(
            () => this.loadData(),
            (error) => {
                console.log(error);
                this.toastr.error("Error: " + error.message);
            },
        );
    }

    public addProjectAlarm(): void {
        const project =
            this.project ??
            Object.assign(new ProjectModel(), {
                id: this.contextService.currentProject,
            });
        const data = { project };
        this.dialogRef = this.openAlarmPanel(data);
        this.dialogRef.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }
            const alarm: ProjectAlarmModel = result.alarm;
            this.projectAlarmService.create(project.id, alarm).subscribe(
                () => this.loadData(),
                (error) => {
                    console.log(error);
                    this.toastr.error("Error: " + error.message);
                },
            );
        });
    }

    private openAlarmPanel(data: any): MatDialogRef<AlarmAddEditComponent> {
        return this._matDialog.open(AlarmAddEditComponent, {
            data,
            panelClass: "right-panel-dialog",
            width: "50vw",
            maxWidth: "100vw",
            height: "100%",
            maxHeight: "100%",
            position: { right: "0", top: "0", bottom: "0" },
            hasBackdrop: true,
            disableClose: false,
        });
    }
}
